"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

type AuthStatus = { authenticated?: boolean; address?: string; token?: string };

const PROTECTED_PREFIXES = [
  "/profile",
  "/report",
  "/flag",
  "/rate",
  "/follow-wallet",
  "/admin",
  "/dashboard",
];

function isProtectedPath(pathname: string) {
  if (!pathname) return false;
  return PROTECTED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export function useSessionRedirect() {
  const pathname = usePathname() || "/";
  const router = useRouter();
  const searchParams = useSearchParams();
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadStatus() {
      try {
        const res = await fetch("/api/auth/status", {
          credentials: "include",
          cache: "no-store",
        });
        const js = (await res.json().catch(() => ({} as AuthStatus))) as AuthStatus;
        if (cancelled) return;
        setAuthenticated(Boolean(js?.authenticated));
      } catch {
        if (cancelled) return;
        setAuthenticated(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    function onAuthChanged() {
      loadStatus();
    }

    loadStatus();
    window.addEventListener("auth-changed", onAuthChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("auth-changed", onAuthChanged);
    };
  }, []);

  useEffect(() => {
    if (!ready) return;

    const redirectTo = searchParams?.get("redirectTo") || "";

    // Strip any redirectTo already on the URL before encoding it as a new
    // one. Without this, bouncing between a protected page and /login (e.g.
    // a token that briefly fails verification) nests redirectTo inside
    // itself on every round trip, growing the URL exponentially.
    function stripRedirectTo(path: string, qsString: string) {
      const sp = new URLSearchParams(qsString);
      sp.delete("redirectTo");
      const qs = sp.toString();
      return qs ? `${path}?${qs}` : path;
    }

    const fullPath = stripRedirectTo(pathname, searchParams?.toString() || "");

    if (!authenticated) {
      if (pathname === "/login") return;
      if (!isProtectedPath(pathname)) return;
      const target = `/login?redirectTo=${encodeURIComponent(fullPath)}`;
      router.replace(target);
      return;
    }

    if (authenticated && pathname === "/login") {
      let target = "/profile";
      if (redirectTo) {
        const [rPath, rQs] = redirectTo.split("?");
        const cleanRedirect = stripRedirectTo(rPath, rQs || "");
        target = `/profile?redirectTo=${encodeURIComponent(cleanRedirect)}`;
      }
      router.replace(target);
    }
  }, [authenticated, pathname, ready, router, searchParams]);
}
